import { useState, useEffect } from "react";
import { AlertCircle, Eye, Trash2, HardDrive, Cpu, MessageSquare, Scan, Layers, RotateCcw, PauseCircle, PlayCircle, Pencil, ChevronDown, MonitorSmartphone } from "lucide-react";
import { formatSize, formatApiErrorDetail } from "../../utils/format";
import {
  fetchSubclients,
  createSubclient,
  updateSubclient,
  deleteSubclient,
  resetPortalPassword,
} from "../../api/admin";
import SsoConfigForm from "./SsoConfigForm";

function Label({ children }) {
  return (
    <div className="block text-sm font-semibold text-stone-500 mb-1.5 uppercase tracking-wider">
      {children}
    </div>
  );
}

function ValueBox({ children, className = "" }) {
  return (
    <div
      className={`px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 text-stone-800 font-medium ${className}`}
    >
      {children}
    </div>
  );
}

export default function ClientReadView({
  client,
  onResetStats,
  onTogglePause,
  onToggleLocalAgent,
}) {
  const [portalPassword, setPortalPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [subclients, setSubclients] = useState([]);
  const [newSubclientName, setNewSubclientName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [promptEditor, setPromptEditor] = useState(null);
  const [promptSaving, setPromptSaving] = useState(false);

  const loadSubclients = async () => {
    try {
      const res = await fetchSubclients(client.id);
      setSubclients(res.data);
    } catch (err) {
      console.error("Erreur chargement sous-clients", err);
    }
  };

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!client?.id) return;
    loadSubclients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleCreateSubclient = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!newSubclientName.trim()) return;

    const slug = newSubclientName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");

    try {
      await createSubclient(client.id, {
        name: newSubclientName,
        slug: slug,
      });
      setNewSubclientName("");
      loadSubclients();
    } catch (err) {
      setErrorMsg(formatApiErrorDetail(err, "Erreur création"));
    }
  };

  const openSubclientPromptEditor = (sub) => {
    const inherited =
      sub.system_prompt != null && String(sub.system_prompt).trim() !== ""
        ? sub.system_prompt
        : client.system_prompt || "";
    setPromptEditor({ subId: sub.id, value: inherited });
  };

  const saveSubclientPrompt = async () => {
    if (!promptEditor) return;
    setPromptSaving(true);
    try {
      await updateSubclient(client.id, promptEditor.subId, {
        system_prompt: promptEditor.value,
      });
      setPromptEditor(null);
      loadSubclients();
    } catch (err) {
      alert(formatApiErrorDetail(err, "Erreur enregistrement du prompt"));
    } finally {
      setPromptSaving(false);
    }
  };

  const resetSubclientPrompt = async () => {
    if (!promptEditor) return;
    if (
      !confirm(
        "Réinitialiser ce prompt pour qu’il suive à nouveau celui de l'entreprise (valeur actuelle de l'entreprise) ?",
      )
    ) {
      return;
    }
    setPromptSaving(true);
    try {
      await updateSubclient(client.id, promptEditor.subId, {
        system_prompt: null,
      });
      setPromptEditor(null);
      loadSubclients();
    } catch (err) {
      alert(formatApiErrorDetail(err, "Erreur réinitialisation"));
    } finally {
      setPromptSaving(false);
    }
  };

  const handleDeleteSubclient = async (subId) => {
    if (
      !confirm(
        "Voulez-vous vraiment supprimer cette équipe ? Ses utilisateurs chatbot associés pourraient perdre l'accès.",
      )
    )
      return;
    try {
      await deleteSubclient(client.id, subId);
      loadSubclients();
    } catch (err) {
      alert(formatApiErrorDetail(err, "Erreur suppression"));
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
        <div>
          <Label>URL du chatbot</Label>
          <ValueBox className="font-mono text-sm text-blue-600">
            /c/{client.slug}
          </ValueBox>
        </div>
        <div>
          <Label>Statut accès</Label>
          <ValueBox className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span
              className={`text-sm font-semibold ${client.is_paused ? "text-amber-700" : "text-emerald-700"}`}
            >
              {client.is_paused ? "En pause (portail + chatbot coupés)" : "Actif"}
            </span>
            <button
              type="button"
              onClick={() =>
                onTogglePause?.(client, !client.is_paused)
              }
              className={`flex items-center gap-1.5 self-start rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors sm:self-auto ${
                client.is_paused
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                  : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
              }`}
            >
              {client.is_paused ? <PlayCircle size={14} /> : <PauseCircle size={14} />}
              {client.is_paused ? "Reprendre" : "Mettre en pause"}
            </button>
          </ValueBox>
        </div>
        <div>
          <Label>Agent local</Label>
          <ValueBox className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span
              className={`text-sm font-semibold ${
                client.local_agent_enabled === false
                  ? "text-stone-500"
                  : "text-emerald-700"
              }`}
            >
              {client.local_agent_enabled === false
                ? "Désactivé (jeton et synchro bloqués)"
                : "Autorisé"}
            </span>
            <button
              type="button"
              onClick={() =>
                onToggleLocalAgent?.(client, client.local_agent_enabled === false)
              }
              className={`flex items-center gap-1.5 self-start rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors sm:self-auto ${
                client.local_agent_enabled === false
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                  : "bg-stone-50 text-stone-700 border-stone-200 hover:bg-stone-100"
              }`}
            >
              <MonitorSmartphone size={14} />
              {client.local_agent_enabled === false ? "Activer" : "Désactiver"}
            </button>
          </ValueBox>
        </div>
        <div>
          <Label>Stockage utilisé</Label>
          <ValueBox>
            <div className="flex justify-between items-center mb-1 text-xs">
              <span className="font-semibold text-stone-700">{formatSize(client.current_storage_bytes || 0)}</span>
              <span className="text-stone-500">/ {formatSize(client.max_storage_bytes || 524288000)}</span>
            </div>
            <div className="w-full bg-stone-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${((client.current_storage_bytes || 0) / (client.max_storage_bytes || 524288000)) > 0.9 ? 'bg-red-500' : 'bg-blue-500'}`} 
                style={{ width: `${Math.min(100, Math.max(0, ((client.current_storage_bytes || 0) / (client.max_storage_bytes || 524288000)) * 100))}%` }}
              ></div>
            </div>
          </ValueBox>
        </div>
        <div>
          <Label>Mot de passe Portail</Label>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <ValueBox className="min-w-0 flex-1 truncate font-mono text-sm">
              {portalPassword
                ? showPassword
                  ? portalPassword
                  : "••••••••"
                : "Non défini (générer un nouveau mot de passe)"}
            </ValueBox>
            <div className="flex shrink-0 items-center gap-2 sm:justify-end">
            {portalPassword && (
              <button
                type="button"
                className="rounded-xl border border-stone-200 bg-stone-50 p-3 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? "Masquer" : "Afficher"}
              >
                <Eye size={18} />
              </button>
            )}
            <button
              type="button"
              className="rounded-xl border border-stone-200 p-3 text-sm font-medium text-blue-600 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
              onClick={async () => {
                try {
                  const res = await resetPortalPassword(client.id);
                  setPortalPassword(res.data.password);
                  setShowPassword(true);
                } catch (err) {
                  alert(
                    formatApiErrorDetail(
                      err,
                      "Erreur lors de la génération du mot de passe",
                    ),
                  );
                }
              }}
            >
              Générer
            </button>
            </div>
          </div>
        </div>
        <div>
          <Label>Couleurs</Label>
          <ValueBox className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span
                className="size-6 rounded-full shadow-inner ring-1 ring-black/10"
                style={{ background: client.primary_color }}
                title={`Primaire: ${client.primary_color}`}
              />
              <span className="text-xs text-stone-500">
                {client.primary_color}
              </span>
            </div>
            <div className="w-px h-4 bg-stone-300"></div>
            <div className="flex items-center gap-2">
              <span
                className="size-6 rounded-full shadow-inner ring-1 ring-black/10"
                style={{ background: client.secondary_color }}
                title={`Secondaire: ${client.secondary_color}`}
              />
              <span className="text-xs text-stone-500">
                {client.secondary_color}
              </span>
            </div>
          </ValueBox>
        </div>
        <div>
          <Label>Modèle LLM</Label>
          <ValueBox>{client.llm_model}</ValueBox>
        </div>
        <div>
          <Label>Température</Label>
          <ValueBox>{client.temperature}</ValueBox>
        </div>
        <div>
          <Label>Max Tokens</Label>
          <ValueBox>{client.max_tokens}</ValueBox>
        </div>
        <div>
          <Label>RAG — Top K (recherche)</Label>
          <ValueBox className="text-sm">
            {client.rag_retrieval_top_k != null
              ? client.rag_retrieval_top_k
              : "Défaut (config globale .env)"}
          </ValueBox>
        </div>
        <div>
          <Label>RAG — Chunks max dans le prompt</Label>
          <ValueBox className="text-sm">
            {client.rag_max_context_chunks != null
              ? client.rag_max_context_chunks
              : "Défaut (config globale .env)"}
          </ValueBox>
        </div>
      </div>

      <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
        <div className="flex items-center justify-between mb-2">
          <Label>Métriques d'utilisation (30 derniers jours)</Label>
          <div className="flex items-center gap-2">
            <button
              onClick={onResetStats}
              className="text-[10px] text-stone-400 hover:text-red-500 bg-white hover:bg-red-50 px-2 py-0.5 rounded border border-stone-200 hover:border-red-200 uppercase font-bold tracking-tighter transition-all flex items-center gap-1"
              title="Réinitialiser les statistiques"
            >
              <RotateCcw size={10} />
              Reset Stats
            </button>
            <span className="text-[10px] text-stone-400 bg-white px-2 py-0.5 rounded border border-stone-200 uppercase font-bold tracking-tighter">Live Stats</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
          <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm">
            <div className="flex items-center gap-2 text-brand-red mb-1">
              <MessageSquare size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">Requêtes</span>
            </div>
            <div className="text-xl font-bold text-stone-800">{client.stats_30d?.queries?.toLocaleString() || 0}</div>
            <div className="text-[10px] text-stone-400 mt-1 italic">Total : {client.total_queries?.toLocaleString() || 0}</div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Cpu size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">Tokens</span>
            </div>
            <div className="text-xl font-bold text-stone-800" title={`Entrée: ${client.stats_30d?.tokens_in?.toLocaleString() || 0} / Sortie: ${client.stats_30d?.tokens_out?.toLocaleString() || 0}`}>
              {client.stats_30d?.tokens?.toLocaleString() || 0}
            </div>
            <div className="text-[10px] text-stone-400 mt-1 italic leading-tight">
              In: {client.stats_30d?.tokens_in?.toLocaleString() || 0} · Out: {client.stats_30d?.tokens_out?.toLocaleString() || 0}
            </div>
            <div className="text-[10px] text-stone-300 mt-1 pt-1 border-t border-stone-50 italic">
              Total Vie : {client.total_tokens?.toLocaleString() || 0}
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <Scan size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">Analyses OCR</span>
            </div>
            <div className="text-xl font-bold text-stone-800">{client.stats_30d?.ocr?.toLocaleString() || 0}</div>
            <div className="text-[10px] text-stone-400 mt-1 italic">Total : {client.total_ocr?.toLocaleString() || 0}</div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Layers size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">Embeddings</span>
            </div>
            <div className="text-xl font-bold text-stone-800">{client.stats_30d?.embeddings?.toLocaleString() || 0}</div>
            <div className="text-[10px] text-stone-400 mt-1 italic">Total : {client.total_embeddings?.toLocaleString() || 0}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm">
            <div className="flex items-center gap-2 text-violet-600 mb-1">
              <MessageSquare size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">Msg. in (30j)</span>
            </div>
            <div className="text-xl font-bold text-stone-800">
              {(client.chat_messages_in_30d ?? 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-stone-400 mt-1 italic">
              Total : {(client.chat_messages_in_total ?? 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm">
            <div className="flex items-center gap-2 text-fuchsia-600 mb-1">
              <MessageSquare size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">Réponses out (30j)</span>
            </div>
            <div className="text-xl font-bold text-stone-800">
              {(client.chat_messages_out_30d ?? 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-stone-400 mt-1 italic">
              Total : {(client.chat_messages_out_total ?? 0).toLocaleString()}
            </div>
          </div>
          {(client.max_chatbot_users != null ||
            client.max_chatbot_user_messages_monthly != null) && (
            <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm md:col-span-2">
              <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">
                Quotas et compteurs
              </div>
              <div className="text-sm text-stone-700 space-y-1">
                <div>
                  <span className="text-stone-500">Utilisateurs chatbot :</span>{" "}
                  <strong>{(client.chatbot_users_count ?? 0).toLocaleString()}</strong>
                  {client.max_chatbot_users != null && (
                    <span className="text-stone-500">
                      {" "}
                      / {client.max_chatbot_users} max
                    </span>
                  )}
                </div>
                <div>
                  <span className="text-stone-500">Questions ce mois :</span>{" "}
                  <strong>
                    {(client.chat_messages_user_month ?? 0).toLocaleString()}
                  </strong>
                  {client.max_chatbot_user_messages_monthly != null && (
                    <span className="text-stone-500">
                      {" "}
                      / {client.max_chatbot_user_messages_monthly} max
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <hr className="border-stone-100" />

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2 lg:gap-8">
        <div>
          <details className="group rounded-2xl border border-stone-200 bg-white shadow-sm open:ring-1 open:ring-stone-200">
            <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold text-stone-700 [&::-webkit-details-marker]:hidden">
              <ChevronDown
                size={18}
                className="shrink-0 text-stone-400 transition-transform group-open:rotate-180"
                aria-hidden
              />
              <span className="uppercase tracking-wider text-stone-500">
                Prompt système
              </span>
              <span className="ml-auto truncate text-right text-xs font-normal text-stone-400">
                {(client.system_prompt || "").length} car. — ouvrir pour lire
              </span>
            </summary>
            <pre className="max-h-[min(55vh,22rem)] overflow-x-auto overflow-y-auto whitespace-pre-wrap border-t border-stone-100 bg-stone-900 p-3 font-mono text-xs leading-relaxed text-stone-300 shadow-inner sm:max-h-[min(60vh,28rem)] sm:p-4 sm:text-sm">
              {client.system_prompt || "—"}
            </pre>
          </details>
        </div>

        <div>
          <Label>Équipes ({subclients.length})</Label>
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:p-6">
            <form
              onSubmit={handleCreateSubclient}
              className="mb-6 flex flex-col gap-2 sm:flex-row sm:gap-3"
            >
              <input
                type="text"
                placeholder="Nom de l'équipe (ex: RH, Tech)"
                value={newSubclientName}
                onChange={(e) => setNewSubclientName(e.target.value)}
                className="flex-1 rounded-xl border border-stone-200 bg-stone-50 px-4 py-2.5 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
              <button
                type="submit"
                className="rounded-xl bg-stone-900 px-5 py-2.5 text-sm font-medium whitespace-nowrap text-white shadow-sm transition-all hover:bg-stone-800 active:scale-[0.98]"
              >
                Ajouter
              </button>
            </form>

            {errorMsg && (
              <p className="text-red-500 text-sm mb-4 bg-red-50 p-3 rounded-lg border border-red-100 flex items-center gap-2">
                <AlertCircle size={16} /> {errorMsg}
              </p>
            )}

            {subclients.length === 0 ? (
              <div className="text-center p-6 bg-stone-50 rounded-xl border border-dashed border-stone-300">
                <p className="text-sm text-stone-500">
                  Aucune équipe n'a été créée.
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {subclients.map((sub) => (
                  <li
                    key={sub.id}
                    className="rounded-xl border border-stone-200 bg-white shadow-sm transition-colors hover:border-stone-300"
                  >
                    <div className="flex flex-col gap-3 p-3 group sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:p-4">
                      <div className="min-w-0 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                        <div className="flex flex-wrap items-center gap-2">
                        <strong className="font-medium text-stone-800">
                          {sub.name}
                        </strong>
                        <span className="rounded-md border border-stone-100 bg-stone-50 px-2 py-0.5 font-mono text-xs text-stone-400 sm:text-sm">
                          {sub.slug}
                        </span>
                        </div>
                        {sub.system_prompt == null ||
                        String(sub.system_prompt).trim() === "" ? (
                          <span className="w-fit rounded-md border border-amber-100 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                            Prompt = entreprise
                          </span>
                        ) : (
                          <span className="w-fit rounded-md border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
                            Prompt dédié
                          </span>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center justify-end gap-1 sm:justify-start">
                        <button
                          type="button"
                          className="rounded-lg p-2 text-stone-500 transition-all hover:bg-stone-100 hover:text-stone-800 sm:opacity-80 sm:group-hover:opacity-100"
                          onClick={() => openSubclientPromptEditor(sub)}
                          title="Prompt système"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          className="rounded-lg p-2 text-stone-400 transition-all hover:bg-red-50 hover:text-red-600 sm:opacity-0 sm:group-hover:opacity-100"
                          onClick={() => handleDeleteSubclient(sub.id)}
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    {promptEditor?.subId === sub.id && (
                      <div className="space-y-3 border-t border-stone-100 bg-stone-50/80 p-4">
                        <p className="text-xs text-stone-500">
                          Variable <code className="rounded bg-white px-1">{"{client_name}"}</code>{" "}
                          remplacée par le nom de l'entreprise. Les nouvelles équipes reçoivent une copie
                          du prompt entreprise à la création ; vous pouvez l’ajuster ici.
                        </p>
                        <textarea
                          value={promptEditor.value}
                          onChange={(e) =>
                            setPromptEditor((p) =>
                              p ? { ...p, value: e.target.value } : p,
                            )
                          }
                          rows={8}
                          className="max-h-[min(50vh,18rem)] w-full resize-y overflow-y-auto rounded-xl border border-stone-200 bg-white p-3 font-mono text-sm text-stone-800 shadow-inner focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:max-h-[min(55vh,22rem)]"
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={promptSaving}
                            onClick={saveSubclientPrompt}
                            className="rounded-xl bg-stone-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-stone-800 disabled:opacity-50"
                          >
                            Enregistrer
                          </button>
                          <button
                            type="button"
                            disabled={promptSaving}
                            onClick={resetSubclientPrompt}
                            className="rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-700 shadow-sm transition-all hover:bg-stone-50 disabled:opacity-50"
                          >
                            Réinitialiser (hériter de l'entreprise)
                          </button>
                          <button
                            type="button"
                            disabled={promptSaving}
                            onClick={() => setPromptEditor(null)}
                            className="rounded-xl px-4 py-2 text-sm font-medium text-stone-500 hover:bg-stone-100"
                          >
                            Fermer
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      {client.id && (
        <SsoConfigForm clientId={client.id} />
      )}
    </div>
  );
}
